<script setup>
import { RichTextRenderer } from '@marvr/storyblok-rich-text-vue-renderer'

const props = defineProps({ blok: Object })
const activeIndex = ref(0)
function onHover(index) {
  activeIndex.value = index
}

const target = ref(null)

const textColorClasses = computed(() => {
  switch (props.blok.color) {
    case 'yellow':
      return '!text-yellow-500'
    case 'blue':
      return '!text-blue-500'
    case 'green':
      return '!text-green-500'
    default:
      return '!text-secondary-red-100 '
  }
})

const bgClasses = computed(() => {
  switch (props.blok.theme) {
    case 'light-gray':
      return 'bg-secondary-gray-50'
    case 'dark-navy':
      return 'bg-primary'
    default :
      return 'bg-white'
  }
})

const paddingClasses = computed(() => {
  switch (props.blok.padding) {
    case 'padding-none':
      return 'py-0'
    case 'padding-top-lg':
      return 'pt-custom-lg'
    case 'padding-bottom-lg':
      return 'pb-custom-lg'
    case 'padding-top-xl':
      return 'pt-custom-3xl'
    case 'padding-bottom-xl':
      return 'pb-custom-3xl'
    case 'padding-top':
      return 'pt-mobile lg:pt-desktop'
    case 'padding-bottom':
      return 'pb-mobile lg:pb-desktop'
    default :
      return 'py-mobile lg:py-desktop'
  }
})

const gridClasses = computed(() => {
  switch (props.blok.grid) {
    case '4-grid':
      return '2xl:grid-cols-4 md:grid-cols-3 sm:grid-cols-2'
    default:
      return 'md:grid-cols-3 sm:grid-cols-2'
  }
})

const classes = computed(() => `${bgClasses.value} ${paddingClasses.value}`)

function getRichTextAlign(attrs) {
  const align = attrs?.textAlign || attrs?.textalign || attrs?.align

  if (['center', 'right', 'left', 'justify'].includes(align))
    return align

  if (typeof attrs?.class === 'string') {
    if (attrs.class.includes('align-center') || attrs.class.includes('text-center'))
      return 'center'
    if (attrs.class.includes('align-right') || attrs.class.includes('text-right'))
      return 'right'
    if (attrs.class.includes('align-left') || attrs.class.includes('text-left'))
      return 'left'
    if (attrs.class.includes('align-justify') || attrs.class.includes('text-justify'))
      return 'justify'
  }

  return ''
}

function markFirstTextNode(nodes, align) {
  let marked = false

  return nodes?.map((node) => {
    if (marked)
      return node

    if (node.type === 'text') {
      marked = true
      return {
        ...node,
        marks: [
          ...(node.marks || []),
          {
            type: 'styled',
            attrs: { class: `richtext-align-${align}` },
          },
        ],
      }
    }

    if (node.content?.length) {
      const content = markFirstTextNode(node.content, align)
      marked = content.some((child, index) => child !== node.content[index])
      return { ...node, content }
    }

    return node
  }) || nodes
}

function normalizeRichTextAlign(node) {
  if (!node)
    return node

  const content = node.content?.map(normalizeRichTextAlign)
  const normalizedNode = content ? { ...node, content } : { ...node }
  const align = getRichTextAlign(normalizedNode.attrs)

  if (align && normalizedNode.content?.length)
    return { ...normalizedNode, content: markFirstTextNode(normalizedNode.content, align) }

  return normalizedNode
}

const richTextContent = computed(() => normalizeRichTextAlign(props.blok.content))
</script>

<template>
  <div :class="classes">
    <div class="full-grid container pt-5 md:pt-10 lg:pt-10">
      <div :class="{ 'rounded-md bg-white px-custom-sm py-custom-lg shadow-custom-xl sm:px-custom-lg': blok.shadow }" class="relative">
        <div
          v-if="blok.header"
          class="mb-1 text-md font-bold capitalize lg:text-xl md:text-xl sm:text-lg" :class="[textColorClasses, { 'text-center': blok.center }, blok.theme === 'dark-navy' ? 'text-white' : 'text-primary']"
        >
          {{ blok.header }}
        </div>
        <div v-if="blok.icon?.filename" class="mb-custom-sm">
          <nuxt-img :src="blok.icon.filename" alt="icon" width="50" height="50" />
        </div>
        <h2 v-if="blok.title" class="mx-auto mb-custom-md text-3xl font-bold md:text-4xl xl:text-5xl" :class="[{ ' text-center': blok.center }, blok.theme === 'dark-navy' ? 'text-white' : 'text-primary']">
          {{ blok.title }}
        </h2>
        <!-- eslint-disable-next-line vue/no-use-v-if-with-v-for -->
        <div v-for="component in blok.components" v-if="blok.components?.length" :key="component.uuid" class="my-custom-lg">
          <StoryblokComponent :blok="component" :theme="blok.color" />
        </div>
        <div v-if="blok.card?.length" class="grid grid-cols-1 gap-x-custom-md gap-y-custom-lg lg:gap-x-custom-lg" :class="[gridClasses]">
          <CardDexonTraining
            v-for="(item, index) in blok.card"
            :key="item.uuid"
            :item="item"
            :slug="item.link.url ? item.link.url : `/${item.link.cached_url}`"
            :active-index="activeIndex"
            :index="index"
            @mouseover="onHover(index)"
          />
        </div>
        <div v-if="blok" ref="target" class="full-grid-richtext columns-content" :class="[{ 'mx-auto max-w-6xl text-center': blok.center }, { 'columns-content-dark': blok.theme === 'dark-navy' }]">
          <RichTextRenderer :document="richTextContent" />
        </div>
        <ul v-if="blok.actions?.length" class="mt-custom-xl flex flex-wrap gap-4 gap-y-custom-md p-0" :class="[{ 'justify-center': blok.center }]">
          <li v-for="action in blok.actions" :key="action.uuid" class="mb-0">
            <StoryblokComponent :blok="action" :theme="blok.color" />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
.full-grid-richtext :deep(p),
.full-grid-richtext :deep(li),
.full-grid-richtext :deep(span),
.full-grid-richtext :deep(h1),
.full-grid-richtext :deep(h2),
.full-grid-richtext :deep(h3),
.full-grid-richtext :deep(h4),
.full-grid-richtext :deep(h5),
.full-grid-richtext :deep(h6) {
  white-space: break-spaces;
}
</style>
